import { SchemaControl, type SchemaControlArgs } from '@/modules/common/models/schema/schema-control.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';
import type { OptionsFilter, OptionsGetter } from '@/modules/common/types/ui.ts';

type SchemaComboboxArgs<T extends string | number, V extends Option<T>> = SchemaControlArgs & {
  placeholder?: string;
  options: V[] | OptionsGetter<T, V>;
  filter?: OptionsFilter<T, V>;
  chooseAfterLoad?: T | null;
  updateAfterLoad?: boolean;
  withCustomOption?: boolean;
  withSearch?: boolean;
  multiple?: boolean;
  clearOnOpen?: boolean;
  expanded?: boolean;
  searchMaxLength?: number;
  pinSelected?: boolean;
  highlightMatches?: boolean;
  isOptionEditable?: (option: V) => boolean;
  editOption?: (option: V) => void;
  addOption?: () => void;
  addOptionLabel?: string;
  singleLine?: boolean;
  searchByCode?: boolean;
  inlineSearch?: boolean;
  noClear?: boolean;
};

export class SchemaCombobox<T extends string | number, V extends Option<T>> extends SchemaControl<V> {
  readonly component = UiCombobox;
  readonly placeholder?: string;
  readonly options: V[] | OptionsGetter<T, V>;
  readonly filter?: OptionsFilter<T, V>;
  readonly chooseAfterLoad?: T | null;
  readonly updateAfterLoad: boolean;
  readonly withCustomOption: boolean;
  readonly withSearch: boolean;
  readonly noClear: boolean;
  readonly multiple: boolean;
  readonly clearOnOpen: boolean;
  readonly expanded: boolean;
  readonly searchMaxLength?: number;
  readonly pinSelected: boolean;
  readonly highlightMatches: boolean;
  readonly isOptionEditable?: (option: V) => boolean;
  readonly editOption?: (option: V) => void;
  readonly addOption?: () => void;
  readonly addOptionLabel?: string;
  readonly singleLine: boolean;
  readonly searchByCode: boolean;
  readonly inlineSearch: boolean;

  constructor(args: SchemaComboboxArgs<T, V>) {
    super(args);

    this.placeholder = args.placeholder;
    this.options = args.options;
    this.filter = args.filter;
    this.chooseAfterLoad = args.chooseAfterLoad;
    this.updateAfterLoad = !!args.updateAfterLoad;
    this.withCustomOption = !!args.withCustomOption;
    this.withSearch = !!args.withSearch;
    this.noClear = args.noClear ?? !!args.required;
    this.multiple = !!args.multiple;
    this.clearOnOpen = !!args.clearOnOpen;
    this.expanded = !!args.expanded;
    this.searchMaxLength = args.searchMaxLength;
    this.pinSelected = !!args.pinSelected;
    this.highlightMatches = !!args.highlightMatches;
    this.isOptionEditable = args.isOptionEditable;
    this.editOption = args.editOption;
    this.addOption = args.addOption;
    this.addOptionLabel = args.addOptionLabel;
    this.singleLine = !!args.singleLine;
    this.searchByCode = !!args.searchByCode;
    this.inlineSearch = !!args.inlineSearch;
  }
}
